import { Typography, Box, useTheme, type BoxProps, type TypographyProps } from '@mui/material';
import type { ReactNode } from 'react';

type FlowLoadingScreenProps = {
    title: string | ReactNode;
    subtitle?: string | ReactNode;
    hideLoadingDots?: boolean;
    slotProps?: {
        box?: BoxProps;
        title?: TypographyProps;
        subtitle?: TypographyProps;
    };
};

function LoadingDots() {
    const theme = useTheme();
    const colors = [theme.palette.purple[200], theme.palette.purple[300], theme.palette.purple[500]];
    const dotCount = 3;
    const animationDuration = 0.8;
    return (
        <Box sx={{ mb: 5 }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                {[...Array(dotCount)].map((_, i) => (
                    <Box
                        // eslint-disable-next-line react/no-array-index-key
                        key={`dot-${i}`}
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            animation: `dotColorCycle ${animationDuration}s infinite linear`,
                            animationDelay: `${(animationDuration / dotCount) * i}s`,
                            '@keyframes dotColorCycle': {
                                '0%': { background: colors[0] },
                                '33%': { background: colors[1] },
                                '66%': { background: colors[2] },
                                '100%': { background: colors[0] },
                            },
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

export default function FlowLoadingScreen({
    title,
    subtitle,
    hideLoadingDots = false,
    slotProps = {},
}: FlowLoadingScreenProps) {
    return (
        <Box
            {...slotProps.box}
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                // `bgcolor`, not the `background` shorthand: a caller overriding this via
                // `slotProps.box.sx` sets `bgcolor` too, and shorthand-vs-longhand ordering made
                // that override a coin flip.
                bgcolor: 'cement.50',
                ...slotProps.box?.sx,
            }}>
            {!hideLoadingDots && <LoadingDots />}

            <Typography
                variant="h1"
                align="center"
                {...slotProps.title}
                sx={{ typography: { xs: 'h3', md: 'h1' }, ...slotProps.title?.sx }}>
                {title}
            </Typography>

            {subtitle ? (
                <Typography
                    align="center"
                    color="text.secondary"
                    {...slotProps.subtitle}
                    sx={{ mt: 2, maxWidth: 480, ...slotProps.subtitle?.sx }}>
                    {subtitle}
                </Typography>
            ) : null}
        </Box>
    );
}

FlowLoadingScreen.LoadingDots = LoadingDots;
